import React, { useState, useEffect, useRef } from "react";
import { Reorder, AnimatePresence, motion } from "framer-motion";
import Word from "./Word";

const Column = ({
    columnNumber,
    totalColumns = 2,
    keyValue,
    languages,
    language_code,
    title,
    initialWords,
    soundsEnabled,
    onWordsReorder,
    sendDataToParent,
    allColumnsState,
    selectedMatchKey,
    onWordSelect,
    onWordLayoutSnapshot,
    onWordWidthMeasurement,
    blinkingWords = [],
    explodingWords = [],
    glowingWords = [],
    sharedWordWidth,
    starBurst,
}) => {
    const safeInitialWords = initialWords || [];
    const [words, setWords] = useState(
        safeInitialWords.map((word) => ({ ...word, isMatched: false }))
    );
    const wordsRef = useRef(safeInitialWords.map((word) => ({ ...word, isMatched: false })));
    const [maxWidth, setMaxWidth] = useState("auto");
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600); // Check screen width
    const containerRef = useRef(null);
    const draggedMatchKeyRef = useRef(null);
    const wordElementByMatchKeyRef = useRef(new Map());

    useEffect(() => {
        const nextWords = safeInitialWords.map((word) => ({ ...word, isMatched: false }));
        wordsRef.current = nextWords;
        setWords(nextWords);
    }, [safeInitialWords]);

    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }

        const animationFrame = window.requestAnimationFrame(() => {
            const textElements = Array.from(containerRef.current.querySelectorAll(".word-text"));

            if (textElements.length === 0) {
                setMaxWidth("auto");
                return;
            }

            const widestTextWidth = Math.max(
                ...textElements.map((element) => Math.ceil(element.scrollWidth || 0))
            );
            const horizontalPadding = 24;
            const audioButtonWidth = soundsEnabled ? 36 : 0;
            const spacingAllowance = soundsEnabled ? 16 : 0;
            const availableWidth = Math.max((containerRef.current.clientWidth || 0) - 24, 0);
            const desiredWidth = widestTextWidth + horizontalPadding + audioButtonWidth + spacingAllowance;
            const calculatedMaxWidth = Math.min(desiredWidth, availableWidth || Number.MAX_SAFE_INTEGER);

            onWordWidthMeasurement?.(language_code, {
                desiredWidth,
                availableWidth,
            });
            setMaxWidth(calculatedMaxWidth > 0 ? `${calculatedMaxWidth}px` : "auto");
        });

        return () => window.cancelAnimationFrame(animationFrame);
    }, [words, soundsEnabled, isMobile, language_code, onWordWidthMeasurement]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 600);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }

        const animationFrame = window.requestAnimationFrame(() => {
            const containerRect = containerRef.current?.getBoundingClientRect();

            if (!containerRect) {
                return;
            }

            const nextLayoutsByMatchKey = {};

            wordElementByMatchKeyRef.current.forEach((element, matchKey) => {
                if (!element) {
                    return;
                }

                const elementRect = element.getBoundingClientRect();
                nextLayoutsByMatchKey[matchKey] = {
                    left: elementRect.left - containerRect.left + elementRect.width / 2,
                    top: elementRect.top - containerRect.top + elementRect.height / 2,
                    width: elementRect.width,
                    height: elementRect.height,
                };
            });

            onWordLayoutSnapshot?.(language_code, nextLayoutsByMatchKey);
        });

        return () => window.cancelAnimationFrame(animationFrame);
    }, [words, maxWidth, isMobile, language_code, onWordLayoutSnapshot]);

    const handleReorder = (newOrder) => {
        setWords(newOrder);
        wordsRef.current = newOrder;
        onWordsReorder(newOrder.map((word) => ({ text: word.text, matchKey: word.matchKey })));
    };

    const measureCurrentWordLayouts = () => {
        if (!containerRef.current) {
            return {};
        }

        const containerRect = containerRef.current.getBoundingClientRect();
        const nextLayoutsByMatchKey = {};

        wordElementByMatchKeyRef.current.forEach((element, matchKey) => {
            if (!element) {
                return;
            }

            const elementRect = element.getBoundingClientRect();
            nextLayoutsByMatchKey[matchKey] = {
                left: elementRect.left - containerRect.left + elementRect.width / 2,
                top: elementRect.top - containerRect.top + elementRect.height / 2,
                width: elementRect.width,
                height: elementRect.height,
            };
        });

        return nextLayoutsByMatchKey;
    };

    const notifyParentOfDrop = (reorderedWords = wordsRef.current) => {
        const otherColumnWords = Object.entries(allColumnsState)
            .filter(([code]) => code !== language_code)
            .map(([, words]) => words)[0];
        const sanitizedWords = reorderedWords.map((word) => ({ text: word.text, matchKey: word.matchKey }));
        const measuredLayouts = measureCurrentWordLayouts();

        if (sendDataToParent) {
            sendDataToParent({
                column_data: {
                    language: language_code,
                    words: sanitizedWords,
                },
                column_layouts: measuredLayouts,
                dropped_match_key: draggedMatchKeyRef.current,
                other_column_data: otherColumnWords,
            });
        }

        draggedMatchKeyRef.current = null;
    };

    const [selectedValue, setSelectedValue] = useState(language_code);

    const handleSelectChange = (event) => {
        setSelectedValue(event.target.value);
        sendDataToParent({
            action: "change_language",
            "changing language to": event.target.value,
            "current language": keyValue,
            "column number": columnNumber,
        });
    };

    const getWordSignature = (text) =>
        text.split("").reduce((sum, character) => sum + character.charCodeAt(0), 0);

    const getExitAnimation = (word, index) => {
        if (explodingWords.some((explosion) => explosion.matchKey === word.matchKey)) {
            return {
                opacity: 0,
                scale: 1,
                x: 0,
                y: 0,
                rotate: 0,
                filter: "blur(0px)",
                transitionEnd: {
                    display: "none",
                },
            };
        }

        const horizontalDirection = columnNumber === 0 ? -1 : 1;
        const verticalDirection = index % 2 === 0 ? -1 : 1;
        const variant = (getWordSignature(word.text) + index) % 3;

        const sharedAnimation = {
            opacity: 0,
            filter: "blur(10px)",
            boxShadow: "0 0 28px rgba(255, 255, 255, 0.95)",
        };

        if (variant === 0) {
            return {
                ...sharedAnimation,
                scale: 1.9,
                rotate: horizontalDirection * 220,
                x: horizontalDirection * (90 + index * 12),
                y: verticalDirection * (35 + index * 6),
            };
        }

        if (variant === 1) {
            return {
                ...sharedAnimation,
                scale: 0.25,
                rotate: horizontalDirection * -320,
                x: horizontalDirection * (55 + index * 8),
                y: -110 - index * 10,
            };
        }

        return {
            ...sharedAnimation,
            scale: 1.45,
            rotate: horizontalDirection * 140,
            x: horizontalDirection * (30 + index * 6),
            y: 120 + index * 14,
            skewX: horizontalDirection * 16,
        };
    };

    const starOffsets = [
        { x: 0, y: -110, scale: 1.6 },
        { x: 92, y: -32, scale: 1.2 },
        { x: 84, y: 58, scale: 1.35 },
        { x: 0, y: 118, scale: 1.5 },
        { x: -88, y: 54, scale: 1.25 },
        { x: -92, y: -28, scale: 1.4 },
        { x: 46, y: -86, scale: 1.05 },
        { x: -42, y: -82, scale: 1.15 },
    ];
    const activeStarOffsets = starOffsets.slice(0, Math.min(4 + (starBurst?.streakCount || 0), starOffsets.length));
    const mobileColumnWidth = `calc((100vw - ${Math.max(totalColumns - 1, 0) * 12 + 16}px) / ${totalColumns})`;
    const visibleWords = words.filter((word) => !word.isMatched);
    const visibleWordCount = Math.max(visibleWords.length, 1);
    const wordOrderSignature = visibleWords.map((word) => word.matchKey).join("|");

    return (
        <div
            className="column-container"
            style={{
                ...styles.columnContainer,
                width: isMobile ? mobileColumnWidth : "200px",
                maxWidth: isMobile ? mobileColumnWidth : "200px",
                margin: isMobile ? "10px 0" : "0 20px",
                "--visible-word-count": visibleWordCount,

            }}
            ref={containerRef}
        >
            <AnimatePresence>
                {starBurst?.token ? (
                    <motion.div
                        key={starBurst.token}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        style={styles.starBurstOverlay}
                    >
                        {activeStarOffsets.map((offset, index) => (
                            <motion.div
                                key={`${starBurst.token}-${index}`}
                                initial={{ opacity: 0, scale: 0.2, x: 0, y: 0, rotate: 0 }}
                                animate={{
                                    opacity: [0, 1, 0],
                                    scale: [0.2, offset.scale, 0.45],
                                    x: [0, offset.x],
                                    y: [0, offset.y],
                                    rotate: [0, 180 + index * 24],
                                }}
                                transition={{
                                    duration: 2.3,
                                    ease: [0.22, 1, 0.36, 1],
                                    delay: index * 0.06,
                                }}
                                style={{
                                    ...styles.starParticle,
                                    color: index % 2 === 0 ? "#ffe066" : "#fff6b7",
                                    textShadow: "0 0 12px rgba(255, 235, 120, 0.95)",
                                }}
                            >
                                ✦
                            </motion.div>
                        ))}
                    </motion.div>
                ) : null}
            </AnimatePresence>
            <AnimatePresence>
                {explodingWords.map((explosion, index) => {
                    const horizontalDirection = columnNumber === 0 ? -1 : 1;
                    const verticalDirection = index % 2 === 0 ? -1 : 1;
                    const spread = 120 + (explosion.matchKey % 5) * 18;
                    const explosionLayout = explosion.layout;

                    return (
                        <motion.div
                            key={explosion.token}
                            initial={{ opacity: 0.95, scale: 1, x: 0, y: 0, rotate: 0, filter: "blur(0px)" }}
                            animate={{
                                opacity: [0.95, 1, 0],
                                scale: [1, 1.28, 0.08],
                                x: [0, horizontalDirection * spread],
                                y: [0, verticalDirection * (50 + (explosion.matchKey % 3) * 26)],
                                rotate: [0, horizontalDirection * (200 + (explosion.matchKey % 4) * 40)],
                                filter: ["blur(0px) brightness(1)", "blur(1px) brightness(1.8)", "blur(16px) brightness(2.4)"],
                            }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
                            style={{
                                ...styles.explosionGhost,
                                left: explosionLayout ? `${explosionLayout.left}px` : "50%",
                                top: explosionLayout ? `${explosionLayout.top}px` : "96px",
                                width: explosionLayout ? `${explosionLayout.width}px` : maxWidth,
                            }}
                        >
                            <div style={styles.explosionCore}>
                                <Word
                                    language_code={language_code}
                                    text={explosion.text}
                                    soundsEnabled={false}
                                    total_width={maxWidth}
                                    isBlinking={false}
                                    isMatchedGlow={false}
                                    isSelected={false}
                                    onSelect={() => {}}
                                />
                            </div>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
            <h2 style={styles.columnTitle}>{title}</h2>
            <Reorder.Group
                axis="y"
                values={words}
                onReorder={(newOrder) => handleReorder(newOrder.map((item) => ({ ...item })))}
                style={styles.list}
            >
                <AnimatePresence initial={false} mode="popLayout">
                    {visibleWords
                        .map((word, index) => (
                            <Reorder.Item
                                key={`${language_code}-${word.matchKey}`}
                                value={word}
                                style={styles.item}
                                layout="position"
                                layoutDependency={wordOrderSignature}
                                ref={(element) => {
                                    if (element) {
                                        wordElementByMatchKeyRef.current.set(word.matchKey, element);
                                    } else {
                                        wordElementByMatchKeyRef.current.delete(word.matchKey);
                                    }
                                }}
                                initial={{ opacity: 0, scale: 0.9, y: 18 }}
                                animate={{ opacity: 1, scale: 1, rotate: 0, x: 0, y: 0, filter: "blur(0px)" }}
                                exit={getExitAnimation(word, index)}
                                onDragStart={() => {
                                    draggedMatchKeyRef.current = word.matchKey;
                                }}
                                onDragEnd={() => notifyParentOfDrop()}
                                transition={{
                                    layout: {
                                        duration: 0.32,
                                        ease: [0.22, 1, 0.36, 1],
                                    },
                                    default: {
                                        duration: 1.8,
                                        ease: [0.22, 1, 0.36, 1],
                                    },
                                }}
                            >
                                <Word
                                    language_code={language_code}
                                    text={word.text}
                                    soundsEnabled={soundsEnabled}
                                    total_width={sharedWordWidth || maxWidth}
                                    isBlinking={blinkingWords.includes(word.matchKey)}
                                    isMatchedGlow={glowingWords.includes(word.matchKey)}
                                    isSelected={selectedMatchKey === word.matchKey}
                                    onSelect={() => onWordSelect?.({ languageCode: language_code, matchKey: word.matchKey })}
                                />
                            </Reorder.Item>
                        ))}
                </AnimatePresence>
            </Reorder.Group>
            <div style={styles.selectorContainer}>
                <select
                    id={`select-${columnNumber}`}
                    onChange={handleSelectChange}
                    style={styles.select}
                    value={language_code}
                >
                    <option selected="true" disabled="disabled">
                        {language_code}
                    </option>
                    {languages.filter((i) => i !== language_code).map((i) => (
                        <option key={i} value={i}>
                            {i}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

const styles = {
    columnContainer: {
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        flexWrap: "wrap",
        paddingBottom: "20px",
        backgroundColor: "#ffffff",
        border: "1px solid #ccc",
        borderRadius: "8px",
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        position: "relative",
        overflow: "visible",
    },
    columnTitle: {
        margin: "10px 0",
        fontSize: "18px",
        fontWeight: "bold",
    },
    list: {
        
        listStyle: "none",
        padding: "0",
        width: "100%",
        display: "flex",
        flexDirection: "column",
    },
    item: {
        display: "flex",
        flex: "0 0 auto",
        justifyContent: "center",
        alignItems: "center",
        padding: "0",
    },
    starBurstOverlay: {
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 4,
    },
    explosionGhost: {
        position: "absolute",
        transform: "translate(-50%, -50%)",
        pointerEvents: "none",
        zIndex: 5,
    },
    explosionCore: {
        boxShadow: "0 0 44px rgba(255, 192, 72, 0.9)",
        borderRadius: "10px",
    },
    starParticle: {
        position: "absolute",
        fontSize: "30px",
        lineHeight: 1,
        willChange: "transform, opacity",
    },
    selectorContainer: {
        marginTop: "20px",
        display: "flex",
        justifyContent: "center",
    },
    select: {
        padding: "5px",
        fontSize: "14px",
        borderRadius: "4px",
        border: "1px solid #ccc",
        textAlign: "center",
    },
};

export default Column;
